<template>
  <el-col :span="24" id="productions">
    <el-row type="flex">
      <el-col :span="24">
        <el-row type="flex" justify="start" align="middle" id="productions-header">
          <HeaderInfoText :colspan="9" title="PRODUCTIONS" :meta="meta" :selection="selection" />
          <el-col :span="6">
            <el-row type="flex" justify="center" v-if="meta.count > 20">
              <el-pagination
                layout="prev, pager, next, jumper"
                :total="meta.count"
                :page-size="20"
                v-model:current-page="currentPage">
              </el-pagination>
            </el-row>
          </el-col>
          <el-col :span="9" class="h-padded right-aligned-col">
            <div v-if="deleting" class="delete-notif">Deleting...</div>
            <el-select
              :disabled="!(selection && selection.length)"
              v-model="batchUpdateSelection"
              placeholder="With selected..."
              style="padding: 0 6px;"
              @change="handleBatchUpdateSelection">
              <el-option
                v-for="option in batchUpdateOptions"
                :key="option.id"
                :label="option.text"
                :value="option.id"
                :disabled="option.disabled">
              </el-option>
            </el-select>
            <!--<el-button type="primary" icon="plus" @click="createNewModel"></el-button>-->
          </el-col>
        </el-row>
        <el-row type="flex" justify="end" align="middle">
          <el-col :span="24">
            <el-table
              v-if="meta.count && !changingPage"
              id="productions-table"
              :ref="`${endpoint}Table`"
              :data="pageData"
              border
              style="width: 100%; z-index: 0;"
              @select="handleTableSelectionChange"
              @select-all="handleTableSelectionChange"
              :row-class-name="tableRowClassName">
              <el-table-column type="selection" width="55" align="left" />
              <el-table-column prop="code" label="Code" align="center" width="120">
                <template v-slot="scope">
                  <el-row type="flex" justify="center">
                    <a class="link-mock" @click="editExistingModel(scope.row.id)">
                      {{ scope.row.code }}
                    </a>
                  </el-row>
                </template>
              </el-table-column>
              <el-table-column prop="name" label="Production" align="left" fit>
                <template v-slot="scope">
                  <el-row type="flex">
                    <a class="link-mock" @click="editExistingModel(scope.row.id)">
                      {{ scope.row.name }}
                    </a>
                  </el-row>
                </template>
              </el-table-column>
            </el-table>
          </el-col>
        </el-row>
      </el-col>
    </el-row>
    <el-dialog
      :title="editMode.active ? hasCreatePermission ? `Edit Production` : `View Production` : 'Create New Production'"
      width="45%"
      v-model="dialogFormVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false">
      <NewEntityForm
        :modalOpen="dialogFormVisible"
        :fields="fields"
        v-model:form="form"
        :idPrefix="'a-'"
        ref="form"
        @formChanged="trackFormChange"
      />
      <template v-slot:footer><span class="dialog-footer">
        <el-button :disabled="saving || !!updating" @click="closeFormModal">Cancel</el-button>
        <el-button :disabled="!hasCreatePermission" :loading="saving || !!updating" type="primary" @click="submitForm">Submit</el-button>
      </span></template>
    </el-dialog>
  </el-col>
</template>

<script>
  import { payload } from '@/components/RouteComponents/Productions/actionPayload'
  import store from '@/store/store'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import getUniqueValues from '@/utils/helpers/GetUniqueValues'
  import {
    handleBatchUpdateSelection,
    createNewModel
  } from '@/components/RouteComponents/commonFunctions'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

  export default {
    components: {
      NewEntityForm,
      HeaderInfoText
    },
    mixins: [ systemViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        batchUpdateOptions: [
          { id: 0, text: 'Delete selected', type: 'delete', disabled: false }
        ],
        batchUpdateSelection: null,
        selection: [],
        dialogFormVisible: false,
        changingPage: false,
        editMode: { active: false, modelId: null }
      }
    },
    methods: {
      /**
       * Handles what happens when the user clicks on one of the "With selected..." dropdown options
       */
      handleBatchUpdateSelection () {
        handleBatchUpdateSelection(this, payload)
      },
      /**
       * Resets editMode data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        createNewModel(this)
      },
      updateBatchUpdateOptions () {
        this.batchUpdateOptions.forEach(option => {
          if (option.type === 'delete') {
            option.disabled = !this.hasDeletePermission
          }
        })
      },
      getUniqueValues
    },
    computed: {
      data () {
        return this.sortData(this.$store.getters.getAllProductions)
      },
      pageData () {
        return this.meetsFilterCriteria(this.data).slice((this.currentPage - 1) * 20, this.currentPage * 20)
      },
      meta () {
        return { count: this.meetsFilterCriteria(this.data).length, shown: this.pageData.length }
      },
      hasCreatePermission () {
        return this.$store.getters.checkIfUserHasRequiredPermissions(['system.view_production', 'system.change_production'])
      },
      hasDeletePermission () {
        return this.hasCreatePermission && this.$store.getters.checkIfUserHasRequiredPermissions(['system.delete_production'])
      }
    },
    watch: {
      pageData () {
        if (this.restoreTableSelection && typeof this.restoreTableSelection === 'function') {
          this.restoreTableSelection()
        }
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      let initPromises = []
      const fieldsAlreadyExist = !typeof store.getters.getResourceFieldsByEndpoint(payload.endpoint) === 'undefined'
      if (!fieldsAlreadyExist) {
        initPromises.push(store.dispatch('fetchOptions', payload))
      }
      try {
        await Promise.all(initPromises)
        if (from.path !== '/') {
          let dataLoader = new DataLoader(store, [ payload ], true, 200)
          await dataLoader.loadData()
        }
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    created () {
      this.endpoint = payload.endpoint
      this.moduleName = payload.moduleName
      this.payload = payload
      this.generateForm()
      this.updateBatchUpdateOptions()
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('productions').offsetHeight
          const headerHeight = document.getElementById('productions-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    }
  }
</script>

<style scoped>
  #productions-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>
